<template>
  <div v-if="isAdminOfCurrentGroup" class="row" id="comp-add-org-members">
    <div class="col-12">
      <div id="card-add-org-members" class="card">
        <div class="card-body">
          <h3 class="card-title">Organizational Groups</h3>
          <pipeline-status-alert
            :pipeline-response-name="responseName"
            for-type="org" for-role="member">
          </pipeline-status-alert>
          <current-members-display
            :current-members="currentOrgMembers"
            member-type="org"
            main-attribute="displayName"
            secondary-attribute="roleId">
          </current-members-display>
          <div class="row">
            <div class="col-md-10 col-lg-8">
              <small id="search-org-groups-help"
                     class="form-text text-muted text-break-word">
                Enter the Organization Code of the group you want to add, if you know it.
                <font-awesome-icon id="org-search-help" icon="question-circle"
                                   v-b-tooltip.hover.html="searchTooltip">
                </font-awesome-icon>
                <br/>Otherwise, choose "Show All" to display all organizational groups.
              </small>
              <div class="row">
                <div class="col-md-8">
                  <!-- Fields for searching for a specific Org -->
                  <div class="input-group">
                    <label for="org-group-search-term" class="sr-only">Search by specific org identifier</label>
                    <input v-model="orgSearch.code"
                           @focus="orgSearch.invalidOrgGroup = false"
                           type="text"
                           class="form-control"
                           id="org-group-search-term"
                           placeholder="Organization Code"
                           maxlength="6">
                    <div class="input-group-append">
                      <button id="btn-org-group-search"
                              class="btn btn-primary"
                              type="button"
                              aria-label="Search for Organization by code"
                              @click="fetchOrgGroup"
                              :disabled="!orgSearch.code || !isValidOrgCodeFormat(orgSearch.code)">
                        <font-awesome-icon v-if="!orgSearch.searching" icon="search"></font-awesome-icon>
                        <font-awesome-icon v-if="orgSearch.searching" icon="spinner" spin></font-awesome-icon>
                      </button>
                    </div>
                  </div>
                  <!-- Show errors for invalid org code format or if Org does not exist in registry -->
                  <div v-if="orgSearch.invalidOrgGroup || isPrefixedSearch" id="org-search-error"
                       class="error-text text-break-word">
                    <span v-if="orgSearch.invalidOrgGroup">
                      Organization org.{{orgLevel}}.{{orgSearch.code}} does not exist
                    </span>
                    <span v-else-if="isPrefixedSearch">Do not include <em>org.</em> or <em>Org Level</em> when
                      searching. Example Org Code: 0001</span>
                  </div>
                  <div v-if="orgsFetch.showAllError" id="org-search-all-error" class="error-text text-break-word">
                    Unable to fetch Org Groups at this time.
                  </div>
                </div>
                <!-- Button to trigger query to fetch ALL Org groups -->
                <div class="col-md-4">
                  <button v-if="!orgsFetch.fetching" id="btn-org-group-all"
                          class="btn btn-primary"
                          type="button"
                          aria-label="Show all Organizational groups"
                          :disabled="orgGroupsFetched"
                          @click="fetchAllOrgGroups">
                    Show All
                  </button>
                  <font-awesome-icon v-if="orgsFetch.fetching"
                                     icon="spinner" spin size="2x" style="color: #861f41">
                  </font-awesome-icon>
                </div>
              </div>
            </div>
          </div>
          <!-- Display results of individual search or fetch all -->
          <template v-if="!isEmpty(orgSearch.orgGroup) || orgsFetch.orgGroups.length > 0">
            <hr>
            <div class="mt-2">
              <!-- Show result of a successful search for a SINGLE Org Group -->
              <template v-if="!isEmpty(orgSearch.orgGroup)">
                <div class="row">
                  <div class="col-md-10 col-lg-8">
                    <div class="row">
                      <div class="col-md-8">
                        <div class="row">
                          <div class="col-12">
                            <span class="group-label text-break-word">Org Name:</span>&nbsp;
                            {{orgSearch.orgGroup.displayName}}
                          </div>
                          <div class="col-12">
                            <span class="group-label group-uugid">Org ID:</span>&nbsp;
                            <span class="group-uugid">{{orgSearch.orgGroup.uugid}}</span>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4 vertical-center">
                        <!-- Show message if searched Org is already a member of the current group -->
                        <span v-if="isCurrentOrgMember" class="small-text">
                          <em><strong>{{orgSearch.orgGroup.displayName}}</strong> is a member of this group</em>
                        </span>
                        <!-- Else display button to add Org to current group -->
                        <button v-else id="btn-add-org"
                                type="button"
                                class="btn btn-primary"
                                :disabled="orgSearch.adding"
                                @click="requestAddSingleOrg">
                          Add Org
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <!-- Show ALL Organizational Groups that are not already members of the current group -->
              <template v-else>
                <div class="row">
                  <div class="col-md-6">
                    <label class="sr-only" for="org-groups-filter">Filter by Code or Name</label>
                    <div class="input-group mb-2">
                      <input v-model="orgGroupsFilter"
                             type="text"
                             class="form-control"
                             id="org-groups-filter"
                             placeholder="Filter by Code or Name">
                      <div class="input-group-append">
                        <button class="btn btn-outline-secondary" type="button" @click="orgGroupsFilter = ''" >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <form @submit.prevent="requestAddMultipleOrgs">
                  <div id="org-groups-list" class="row scroll-container">
                    <div class="form-group col-md-6"
                         v-for="orgGroup in filteredAvailableOrgGroups"
                         :key="orgGroup.uugid">
                      <div class="custom-control custom-checkbox">
                        <label :for="'chk-org-group-'+orgGroup.uugid" class="sr-only">{{orgGroup.uugid}}</label>
                        <!--suppress HtmlFormInputWithoutLabel lint checks do not pick up the dynamic id-->
                        <input class="custom-control-input"
                               type="checkbox"
                               v-model="addOrgMembersForm.members"
                               :value="{uugid: orgGroup.uugid, displayName: orgGroup.displayName}"
                               :id="'chk-org-group-'+orgGroup.uugid"
                               :disabled="addOrgMembersForm.submitting">
                        <label class="custom-control-label" :for="'chk-org-group-'+orgGroup.uugid">
                          <span class="font-weight-bold">Name:</span>&nbsp;{{orgGroup.displayName}}
                          <br/>
                          <span class="font-weight-bold">ID:</span>&nbsp;
                          <span class="group-uugid">{{orgGroup.uugid}}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  <button id="btn-add-org-grp-members"
                          type="submit"
                          class="btn btn-primary"
                          :disabled="addOrgMembersForm.members.length < 1 ||
                            addOrgMembersForm.submitting === true">
                    {{addOrgMembersForm.submitting === true ? 'Submitting...' : 'Add Selected Orgs'}}
                  </button>
                </form>
                <template v-if="addOrgMembersForm.members.length > 0">
                  <div id="selected-orgs" class="row small">
                    <div class="col-12">Selected Orgs:</div>
                    <div class="col-12">{{orgsToAdd}}</div>
                  </div>
                </template>
              </template>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { eventBus } from '../../main';
import { addOrgMixins } from '../../util/add-org-members-mixin';
import { util } from '../../util/groups-mixin';
import PipelineStatusAlert from '../PipelineStatusAlert';
import CurrentMembers from './CurrentMembers';

export default {
  name: 'AddOrgMembers',
  mixins: [addOrgMixins, util],
  props: {
    responseName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      addOrgMembersForm: {
        members: [],
        submitting: false,
      },
      /* HTML to be displayed in Org Search tooltip */
      searchTooltip:
      '<div style="text-align: left !important; font-size: 90%">Example Org Codes:' +
        '<div style="margin-left: .5em;">' +
          '<div><strong>Sr Mgt Level:</strong> s01</div>' +
          '<div><strong>Mgt Level:</strong> m001</div>' +
          '<div><strong>Dept Level:</strong> 0001</div>' +
          '<div><strong>Org Level:</strong> 123456, 1234ab</div>' +
        '</div>' +
      '</div>',
      /* Properties associated with searching/adding a single Organizational Group */
      orgSearch: {
        code: '',
        orgGroup: {},
        searching: false,
        invalidOrgGroup: false,
        adding: false,
      },
      /* Properties associated with searching/adding multiple Organizational Groups */
      orgsFetch: {
        orgGroups: [],
        fetching: false,
        fetched: false,
        showAllError: false,
      },
      orgGroupsFilter: '',
    };
  },
  components: {
    'pipeline-status-alert': PipelineStatusAlert,
    'current-members-display': CurrentMembers,
  },
  computed: {
    /** When searching for a specific Organizational Group, org level will be calculated based on user input for
     * the Organization code.
     * */
    orgsToAdd() {
      return this.formattedOrgsToAddString(this.addOrgMembersForm.members);
    },
    /**
     * Calculate the Org level based on the code entered by the user. Each level has a specific code format.
     * */
    orgLevel() {
      return this.getOrgLevel(this.orgSearch.code);
    },
    orgGroupsFetched() {
      return this.orgsFetch.fetched && this.orgsFetch.orgGroups.length > 1;
    },
    groupMemberIds() {
      return this.$store.getters.groupMemberIds(this.managedGroup);
    },
    availableOrgGroupMembers() {
      return this.getAvailableOrgMembers(this.orgsFetch.orgGroups, this.groupMemberIds);
    },
    filteredAvailableOrgGroups() {
      return this.getFilteredAvailableOrgGroups(this.orgGroupsFilter, this.availableOrgGroupMembers);
    },
    currentOrgMembers() {
      return this.$store.getters.organizationalGroupMembers(this.managedGroup);
    },
    /**
     * Check if the Org being searched (individual org search) is already a member of the current group.
     * */
    isCurrentOrgMember() {
      return this.isOrgMember(this.currentOrgMembers, this.orgSearch.orgGroup.uugid);
    },
    isPrefixedSearch() {
      return this.doRegexTest(/^(org\.).*/, this.orgSearch.code);
    },
  },
  methods: {
    /**
     * Dispatch store action to add org groups.
     *
     * @param roleIds array of org group ids to add.
     * @returns Resolved promise.
     * */
    submitAddOrgs(roleIds) {
      this.$store.dispatch('clearPipelinedStateResponse', 'addOrgMembersResponse');
      return this.$store.dispatch('batchModifyGroupRoles',
        {
          uugid: this.managedGroup.uugid,
          type: 'members',
          kind: 'group',
          roleIds,
          modify: 'add',
          responseToSet: 'addOrgMembersResponse',
        });
    },
    /**
     * Method for adding a single Org group when a user searches for a specific Org and then chooses to add it to the
     * current group.
     * */
    requestAddSingleOrg() {
      this.orgSearch.adding = true;
      this.submitAddOrgs([this.orgSearch.orgGroup.uugid])
        .finally(() => {
          this.resetOrgGroupSearch();
          this.scrollToElement('sta-pipe-success-org-member');
        });
    },
    /**
     * Method for adding Org groups that are selected from the list of groups when a user chooses "Show All".
     * */
    requestAddMultipleOrgs() {
      const vm = this;
      vm.addOrgMembersForm.submitting = true;
      const orgIds = this.addOrgMembersForm.members.map(member => member.uugid);
      this.submitAddOrgs(orgIds)
        .finally(() => {
          vm.resetAddOrgMembersForm();
          document.getElementById('org-groups-list').scrollTop = 0;
          vm.scrollToElement('sta-pipe-success-org-member');
        });
    },
    /**
     * Reset the form data as generated by the "Show All" functionality.
     * */
    resetAddOrgMembersForm() {
      this.addOrgMembersForm.members = [];
      this.addOrgMembersForm.submitting = false;
    },
    /**
     * Reset all attributes related to the user searching/adding a single Org group.
     * */
    resetOrgGroupSearch() {
      this.orgSearch.orgGroup = {};
      this.orgSearch.code = '';
      this.orgSearch.searching = false;
      this.orgSearch.invalidOrgGroup = false;
      this.orgSearch.adding = false;
      this.orgSearch.showAllError = false;
    },
    /**
     * Reset all attributes related to the user showing all/adding multiple Org groups.
     * */
    resetOrgGroupsSearch() {
      this.orgsFetch.orgGroups = [];
      this.orgGroupsFilter = '';
      this.orgsFetch.fetching = false;
      this.orgsFetch.showAllError = false;
      this.resetAddOrgMembersForm();
    },
    /**
     * Get ALL Org groups from ED.
     * */
    fetchAllOrgGroups() {
      const vm = this;
      this.resetOrgGroupSearch();
      this.orgsFetch.fetching = true;
      this.orgsFetch.showAllError = false;
      this.$store.dispatch('fetchOrgGroupsFromEd')
        .then((response) => {
          vm.orgsFetch.orgGroups = response;
        })
        .catch(() => {
          eventBus.clearStatus();
          vm.orgsFetch.showAllError = true;
        })
        .finally(() => {
          vm.orgsFetch.fetching = false;
        });
    },
    /**
     * Get a specific group based on an Org code provided by the user.
     * */
    fetchOrgGroup() {
      const vm = this;
      this.resetOrgGroupsSearch();
      this.orgSearch.searching = true;
      this.$store.dispatch('fetchOrgGroupsFromEd', { uugid: `org.${this.orgLevel}.${this.orgSearch.code}` })
        .then((response) => {
          vm.orgSearch.orgGroup = response;
          vm.orgSearch.code = '';
        })
        .catch((error) => {
          if (error.response.status === 404) {
            eventBus.clearStatus();
            vm.orgSearch.invalidOrgGroup = true;
          } else {
            vm.scrollToElement('sta-alert');
          }
        })
        .finally(() => {
          vm.orgSearch.searching = false;
        });
    },
  },
};
</script>

<style scoped>
  .small-text {
    font-size: 90% !important;
  }
  .group-label {
    font-weight: 600;
  }
  .error-text {
    color: red !important;
    font-weight: bold;
  }
  #selected-orgs {
    margin: .5em .25em;
  }
  @media (max-width: 767px) {
    #btn-org-group-all, #btn-add-org, svg {
      margin-top: 0.5em;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/add-members/AddOrgMembers.vue